import React, { useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import Button from '../../components/common/Button';
import { ExerciseActivityTargetDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFileApi } from '../../hooks/useFileApi';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface ExerciseActivityTargetFormProps {
  initialValues: ExerciseActivityTargetDTO;
  onSubmit: (data: ExerciseActivityTargetDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  image: {
    id: 'image',
    title: 'Target Image',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const ExerciseActivityTargetForm: React.FC<ExerciseActivityTargetFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] =
    useState<ExerciseActivityTargetDTO>(initialValues);
  const { uploadFile } = useFileApi();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    imageUrl: [{ required: formData.isUrl, message: 'Url is required' }],
    displayPosition: [{ required: true, message: 'Position is required' }],
  };

  const { errors, validate } =
    useFormValidation<ExerciseActivityTargetDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange = (field: keyof ExerciseActivityTargetDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'activities',
        );
        if (response && response.success && response.data) {
          setFormData((prev) => ({
            ...prev,
            imageUrl: response.data,
            imageId: response.data, // Save the file ID
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.image.id}
        title={FORM_SECTIONS.image.title}
        columns={1}
      >
        <FormField
          id="isUrl"
          label="Is URL"
          type="checkbox"
          value={formData.isUrl || false}
          onChange={(e) => handleChange(e, setFormData)}
        />
        {formData.isUrl ? (
          <FormField
            id="imageUrl"
            label="URL Path"
            type="text"
            value={formData.imageUrl || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter external URL"
            required
            error={errors.imageUrl}
          />
        ) : (
          <>
            {formData.imageId && (
              <ImageLink fileId={formData.imageId} label="Target Image" />
            )}
            <div className="flex gap-4">
              <div className="flex-grow">
                <FileUpload
                  id="imageUpload"
                  onChange={handleFileChange('imageId')}
                  title="Upload target image"
                  accept={FileTypeConstants.ALL_SUPPORTED}
                  isUploading={uploadingFiles['imageId']}
                />
              </div>
            </div>

            <FormField
              id="isPortrait"
              label="Portrait?"
              type="checkbox"
              value={formData.isPortrait || false}
              onChange={(e) => handleChange(e, setFormData)}
              helper="Check to make target portrait"
            />
          </>
        )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || false}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active"
        />
      </FormSection>
    </Form>
  );
};

export default ExerciseActivityTargetForm;
